import type { NotificationItem } from '../api/notifications';
import type { PushNotificationType } from '../api/push';

export type NotificationActivityEntityType =
  | 'PLAN'
  | 'COMPLETION_REPORT'
  | 'POST_MORTEM'
  | 'CO_ACTION'
  | 'CODE_REVIEW'
  | 'DOCUMENT'
  | 'CONVENTION'
  | 'SKILL';

export type NotificationActivityTarget = {
  entityType: NotificationActivityEntityType;
  id: string;
  projectId: string;
};

export type NotificationRunnerRequestTarget = {
  id: string;
  projectId: string | null;
};

type NotificationDestination =
  | { kind: 'activity'; entityType: NotificationActivityEntityType }
  | { kind: 'runnerRequest' }
  | { kind: 'featureList'; pathname: '/documents' | '/conventions' | '/skills' }
  | { kind: 'notificationFallback' };

export const notificationDestinationByType: Record<PushNotificationType, NotificationDestination> = {
  comment: { kind: 'activity', entityType: 'PLAN' },
  'completion-report': { kind: 'activity', entityType: 'COMPLETION_REPORT' },
  'post-mortem': { kind: 'activity', entityType: 'POST_MORTEM' },
  'co-action-created': { kind: 'activity', entityType: 'CO_ACTION' },
  'code-review-completed': { kind: 'activity', entityType: 'CODE_REVIEW' },
  'daemon-trigger-status-changed': { kind: 'runnerRequest' },
  'convention-created': { kind: 'activity', entityType: 'CONVENTION' },
  'convention-updated': { kind: 'activity', entityType: 'CONVENTION' },
  'convention-deleted': { kind: 'featureList', pathname: '/conventions' },
  'skill-created': { kind: 'activity', entityType: 'SKILL' },
  'skill-updated': { kind: 'activity', entityType: 'SKILL' },
  'skill-deleted': { kind: 'featureList', pathname: '/skills' },
  'document-created': { kind: 'activity', entityType: 'DOCUMENT' },
  'document-updated': { kind: 'activity', entityType: 'DOCUMENT' },
  'document-deleted': { kind: 'featureList', pathname: '/documents' },
  'document-comment': { kind: 'activity', entityType: 'DOCUMENT' },
  'plan-created': { kind: 'activity', entityType: 'PLAN' },
  'plan-status-changed': { kind: 'activity', entityType: 'PLAN' },
  'runner-update-completed': { kind: 'notificationFallback' },
  'runner-update-applied': { kind: 'notificationFallback' },
  'runner-update-failed': { kind: 'notificationFallback' },
};

const getNotificationDestination = (type: string): NotificationDestination | null =>
  notificationDestinationByType[type as PushNotificationType] ?? null;

export const getNotificationActivityTarget = (
  notification: Pick<NotificationItem, 'type' | 'entityId'> & {
    projectId: string | null;
    relatedPlanId?: string | null;
  },
): NotificationActivityTarget | null => {
  const destination = getNotificationDestination(notification.type);
  if (destination?.kind !== 'activity' || !notification.projectId) return null;
  const entityType = destination.entityType;

  const id =
    entityType === 'PLAN' && notification.type === 'comment'
      ? (notification.relatedPlanId ?? null)
      : notification.entityId;

  if (!id) return null;

  return {
    entityType,
    id,
    projectId: notification.projectId,
  };
};

export const getNotificationRunnerRequestTarget = (
  notification: Pick<NotificationItem, 'type' | 'entityId'> & { projectId: string | null },
): NotificationRunnerRequestTarget | null => {
  const destination = getNotificationDestination(notification.type);
  if (destination?.kind !== 'runnerRequest' || !notification.entityId) return null;

  return {
    id: notification.entityId,
    projectId: notification.projectId,
  };
};

export const getNotificationFeatureListHref = (
  notification: Pick<NotificationItem, 'type' | 'project'> & { projectId: string | null },
): string | null => {
  const destination = getNotificationDestination(notification.type);
  if (destination?.kind !== 'featureList') return null;

  const params = new URLSearchParams();
  if (notification.project?.teamId && notification.projectId) {
    params.set('teamId', notification.project.teamId);
    params.set('projectId', notification.projectId);
  }
  const query = params.toString();
  return query ? `${destination.pathname}?${query}` : destination.pathname;
};

export const buildHomeNotificationHref = (
  notification: Pick<NotificationItem, 'id' | 'type' | 'entityId' | 'project'> & {
    projectId: string | null;
    relatedPlanId?: string | null;
  },
) => {
  const activityTarget = getNotificationActivityTarget(notification);
  const runnerRequestTarget = getNotificationRunnerRequestTarget(notification);
  const featureListHref = getNotificationFeatureListHref(notification);
  if (featureListHref) return featureListHref;
  const params = new URLSearchParams(
    runnerRequestTarget
      ? { runnerRequest: runnerRequestTarget.id }
      : activityTarget
        ? { activity: `${activityTarget.entityType}:${activityTarget.id}:${activityTarget.projectId}` }
        : { notification: notification.id },
  );

  if (notification.project?.teamId && notification.projectId) {
    params.set('teamId', notification.project.teamId);
    params.set('projectId', notification.projectId);
  }
  return `/home?${params.toString()}`;
};
